import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Play, 
  Pause,
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertTriangle,
  RefreshCw,
  Calendar,
  Timer,
  Settings2,
  Database,
  FileText,
  Sparkles,
  ShoppingCart,
  Receipt,
  Boxes,
  ArrowRightLeft,
  Package,
  Lock,
  Unlock,
  Power,
  PowerOff,
  Edit,
  Save,
  RotateCcw,
  Plus,
  X
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Job metadata with icons and categories
const SUMMARY_LABELS = {
  docs_found: 'docs', lines_saved: 'linhas', series_processed: 'séries', synced: 'POs', updated_items: 'linhas atualizadas',
  satisfaction_docs_found: 'GEF/CCF', series_scanned: 'séries', total_documents: 'docs', total_matches: 'faturas',
  sas_synced: 'marcações sinc.', wos_synced: 'ordens sinc.', sas_failed: 'marcações falhadas', webhooks_reprocessed: 'avisos reprocessados',
  created: 'criados', updated: 'atualizados', pending: 'pendentes', failed: 'falhados',
  products_synced: 'artigos', products_created: 'criados', products_updated: 'atualizados', articles_updated: 'atualizados',
  suggestions_created: 'sugestões', products_analyzed: 'artigos analisados', recovered: 'passos recuperados',
};

const STEP_NAMES = { demand_etl: 'ETL', procurement_intelligence: 'AI', po_status_sync: 'PO', ap_invoice_sync: 'AP' };

const CatalogTestButton = () => {
  const [busy, setBusy] = useState(false);
  const poll = async () => {
    let data = { status: 'running' };
    for (let i = 0; i < 120 && data.status === 'running'; i += 1) {
      await new Promise((r) => setTimeout(r, 5000));
      ({ data } = await axios.get(`${API}/admin/scheduler/fsm_catalog_sync/test`));
    }
    const res = data.result || {};
    if (data.status === 'running') toast.info('O teste continua a correr; volte a abrir esta página daqui a pouco');
    else if (data.status === 'error' || res.failed) toast.error(`Falhou (${res.failed || 0} artigos): ${res.error || (res.errors || []).join(' | ')}`, { duration: 20000 });
    else toast.success(`Feito no Zoho: ${(res.done_skus || []).join(', ') || 'nada a fazer'}`, { duration: 20000 });
  };
  useEffect(() => {  // resume if a test is still running (e.g. page reloaded)
    axios.get(`${API}/admin/scheduler/fsm_catalog_sync/test`).then(({ data }) => {
      if (data.status === 'running') { setBusy(true); poll().finally(() => setBusy(false)); }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    if (!window.confirm('Criar/atualizar no Zoho FSM apenas 5 artigos, para verificar?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/scheduler/fsm_catalog_sync/test`);
      await poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro no teste');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" variant="outline" className="mt-1 h-7 text-xs" onClick={run} disabled={busy} data-testid="fsm-catalog-test-btn">
      {busy ? 'A testar…' : 'Testar com 5 artigos'}
    </Button>
  );
};

const JobRunDetails = ({ job }) => {
  const run = job.last_run || {};
  const summary = run.summary || {};
  const parts = Object.entries(SUMMARY_LABELS)
    .filter(([k]) => typeof summary[k] === 'number')
    .map(([k, label]) => `${summary[k]} ${label}`);
  const skippedAfterRun = job.last_skipped && (!run.started_at || job.last_skipped.at > run.started_at);
  return (
    <div className="mt-1 space-y-0.5 max-w-[220px]">
      {parts.length > 0 && <p className="text-xs text-slate-600" data-testid={`job-summary-${job.job_id}`}>{parts.join(' · ')}</p>}
      {run.status === 'failed' && run.error && <p className="text-xs text-red-600 line-clamp-2" title={run.error}>{run.error}</p>}
      {run.steps?.length > 0 && (
        <p className="text-xs text-slate-500">{run.steps.map(s => `${STEP_NAMES[s.step] || s.step}: ${s.status}`).join(' · ')}</p>
      )}
      {skippedAfterRun && (
        <p className="text-xs text-amber-700" data-testid={`job-skipped-${job.job_id}`}>
          Ignorado {new Date(job.last_skipped.at).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}: {job.last_skipped.reason}
        </p>
      )}
    </div>
  );
};


const JOB_METADATA = {
  // === CONSOLIDATED ===
  daily_sync: {
    icon: RefreshCw,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    category: 'Sync Consolidado',
    schedule: 'Diário às 06:30',
    highlight: true,
  },
  // === PROCUREMENT AI ===
  demand_etl: {
    icon: FileText,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    category: 'Procurement AI',
    schedule: 'Diário às 06:30',
  },
  procurement_intelligence: {
    icon: Sparkles,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    category: 'Procurement AI',
    schedule: 'Após Demand ETL',
  },
  // === SAGE ===
  sage_auto_sync: {
    icon: Database,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    category: 'SAGE',
    schedule: 'A cada 15 min (8h-19h)',
  },
  po_status_sync: {
    icon: ShoppingCart,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    category: 'SAGE',
    schedule: 'A cada 15 min (8h-19h)',
  },
  ap_invoice_sync: {
    icon: Receipt,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    category: 'SAGE',
    schedule: 'A cada 30 min (8h-19h)',
  },
  stock_reconciliation: {
    icon: ArrowRightLeft,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-50',
    category: 'SAGE',
    schedule: 'A cada 2 horas',
  },
  fsm_reconciliation: {
    icon: ArrowRightLeft,
    color: 'text-violet-500',
    bgColor: 'bg-violet-50',
    category: 'Zoho FSM',
    schedule: 'A cada 15 min',
  },
  fsm_catalog_sync: {
    icon: Package,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    category: 'Zoho FSM',
    schedule: '05:00 + após cada Stock Sync',
  },
};

const SyncManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [lockStatus, setLockStatus] = useState(null);
  const [sageLock, setSageLock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    type: 'interval',
    // For cron with multiple times
    cronTimes: ['06:30'],  // Array of HH:MM strings
    // For interval
    minutes: 15,
    hours: 2,
    hour_start: 8,
    hour_end: 19,
    day_of_week: 'mon-sat',
  });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const fetchJobStatus = useCallback(async () => {
    try {
      const [jobsRes, lockRes] = await Promise.all([
        axios.get(`${API}/admin/scheduler/status`),
        axios.get(`${API}/admin/sage-lock-status`).catch(() => ({ data: null })),
      ]);
      
      setJobs(jobsRes.data.jobs || []);
      setLockStatus(jobsRes.data.lock_status);
      setSageLock(lockRes.data);
    } catch (error) {
      console.error('Error fetching job status:', error);
      toast.error('Erro ao carregar estado dos jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobStatus();
    const interval = setInterval(fetchJobStatus, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [fetchJobStatus]);

  const runJob = async (jobId) => {
    setRunningJob(jobId);
    try {
      const response = await axios.post(`${API}/admin/scheduler/${jobId}/run`);
      if (response.data.success) {
        toast.success(`Job "${getJobName(jobId)}" iniciado com sucesso`);
        fetchJobStatus();
      } else {
        toast.error(response.data.result?.message || 'Erro ao executar job');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar job');
    } finally {
      setRunningJob(null);
    }
  };

  const toggleJob = async (jobId, enable) => {
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await axios.post(`${API}/admin/scheduler/${jobId}/${endpoint}`);
      toast.success(`Job "${getJobName(jobId)}" ${enable ? 'activado' : 'desactivado'}`);
      fetchJobStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar estado do job');
    }
  };

  const forceReleaseLock = async () => {
    try {
      await axios.post(`${API}/admin/sage-lock/force-release`);
      toast.success('Lock SAGE libertado com sucesso');
      fetchJobStatus();
      setShowConfirmDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao libertar lock');
    }
  };

  const cancelStaleSyncs = async () => {
    try {
      const res = await axios.post(`${API}/admin/sage-sync/cancel-stale`);
      toast.success(`${res.data.cancelled || 0} sync(s) cancelado(s)`);
      fetchJobStatus();
      setShowConfirmDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar syncs');
    }
  };

  const openScheduleEditor = async (jobId) => {
    try {
      const res = await axios.get(`${API}/admin/scheduler/${jobId}/schedule`);
      const schedule = res.data.schedule || {};
      
      setEditingJob({
        id: jobId,
        name: res.data.job_name,
        isDefault: res.data.is_default,
      });
      
      // Convert single hour/minute to cronTimes array, or use existing cronTimes
      let cronTimes = schedule.cronTimes || [];
      if (cronTimes.length === 0 && schedule.type === 'cron') {
        const h = (schedule.hour ?? 6).toString().padStart(2, '0');
        const m = (schedule.minute ?? 30).toString().padStart(2, '0');
        cronTimes = [`${h}:${m}`];
      }
      if (cronTimes.length === 0) {
        cronTimes = ['06:30'];
      }
      
      setScheduleForm({
        type: schedule.type || 'interval',
        cronTimes: cronTimes,
        minutes: schedule.minutes ?? 15,
        hours: schedule.hours ?? 2,
        hour_start: schedule.hour_start ?? 8,
        hour_end: schedule.hour_end ?? 19,
        day_of_week: schedule.day_of_week || 'mon-sat',
      });
      
      setShowScheduleDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar configuração do horário');
    }
  };

  const saveSchedule = async () => {
    if (!editingJob) return;
    
    setSavingSchedule(true);
    try {
      const scheduleData = { type: scheduleForm.type };
      
      if (scheduleForm.type === 'cron') {
        // Filter out empty times and sort
        const validTimes = scheduleForm.cronTimes
          .filter(t => t && t.trim())
          .sort();
        
        if (validTimes.length === 0) {
          toast.error('Adicione pelo menos uma hora');
          setSavingSchedule(false);
          return;
        }
        
        // Store all times in cronTimes array
        scheduleData.cronTimes = validTimes;
        // For backwards compatibility, also set hour/minute from first time
        const [h, m] = validTimes[0].split(':');
        scheduleData.hour = parseInt(h);
        scheduleData.minute = parseInt(m);
        scheduleData.day_of_week = scheduleForm.day_of_week;
      } else if (scheduleForm.type === 'interval') {
        if (scheduleForm.hours && scheduleForm.hours > 0) {
          scheduleData.hours = parseInt(scheduleForm.hours);
        } else {
          scheduleData.minutes = parseInt(scheduleForm.minutes);
        }
        scheduleData.hour_start = parseInt(scheduleForm.hour_start);
        scheduleData.hour_end = parseInt(scheduleForm.hour_end);
        scheduleData.day_of_week = scheduleForm.day_of_week;
      }
      
      const res = await axios.put(`${API}/admin/scheduler/${editingJob.id}/schedule`, scheduleData);
      
      if (res.data.hot_reload_applied) {
        toast.success(res.data.message || 'Horário actualizado e aplicado!');
      } else {
        toast.success(res.data.message || 'Horário actualizado');
        if (res.data.requires_restart) {
          toast.info('Reinicie a aplicação para aplicar as alterações', { duration: 5000 });
        }
      }
      
      setShowScheduleDialog(false);
      fetchJobStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar horário');
    } finally {
      setSavingSchedule(false);
    }
  };

  const resetSchedule = async () => {
    if (!editingJob) return;
    
    try {
      await axios.post(`${API}/admin/scheduler/${editingJob.id}/schedule/reset`);
      toast.success('Horário reposto para default');
      setShowScheduleDialog(false);
      fetchJobStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao repor horário');
    }
  };

  const getJobName = (jobId) => {
    const job = jobs.find(j => j.job_id === jobId);
    return job?.name || jobId;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'running':
        return <Badge className="bg-blue-500"><Loader2 className="h-3 w-3 mr-1 animate-spin" />A executar</Badge>;
      case 'completed':
      case 'success':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Concluído</Badge>;
      case 'partial':
        return <Badge className="bg-amber-500"><AlertTriangle className="h-3 w-3 mr-1" />Parcial</Badge>;
      case 'unknown':
      case undefined:
      case null:
        return <Badge variant="outline">Sem registo</Badge>;
      case 'failed':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Erro</Badge>;
      case 'idle':
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Inactivo</Badge>;
      case 'skipped':
        return <Badge variant="secondary"><Pause className="h-3 w-3 mr-1" />Ignorado</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Desconhecido'}</Badge>;
    }
  };

  const groupedJobs = jobs.reduce((acc, job) => {
    const meta = JOB_METADATA[job.job_id] || {};
    const category = meta.category || 'Outros';
    if (!acc[category]) acc[category] = [];
    acc[category].push(job);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6" />
            Gestão de Sincronizações
          </h1>
          <p className="text-slate-500 mt-1">
            Controlo centralizado de todos os processos automáticos e manuais
          </p>
        </div>
        <Button onClick={fetchJobStatus} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* SAGE Lock Status Card */}
      {sageLock && sageLock.lock_held && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 flex items-center gap-2 text-base">
              <Lock className="h-4 w-4" />
              SAGE Ocupado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1 text-sm">
                <p><span className="text-slate-500">Operação:</span> <span className="font-medium">{sageLock.lock_name}</span></p>
                <p><span className="text-slate-500">Iniciado por:</span> <span className="font-medium">{sageLock.initiated_by_name || sageLock.initiated_by || 'Desconhecido'}</span></p>
                <p><span className="text-slate-500">Desde:</span> <span className="font-medium">{formatDateTime(sageLock.acquired_at)}</span></p>
                <p><span className="text-slate-500">Expira:</span> <span className="font-medium">{formatDateTime(sageLock.expires_at)}</span></p>
              </div>
              <div className="flex flex-col gap-2">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => {
                    setConfirmAction('release');
                    setShowConfirmDialog(true);
                  }}
                >
                  <Unlock className="h-4 w-4 mr-1" />
                  Forçar Libertação
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setConfirmAction('cancel');
                    setShowConfirmDialog(true);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Cancelar Sync Stale
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Jobs by Category */}
      {Object.entries(groupedJobs).map(([category, categoryJobs]) => (
        <Card key={category}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {category === 'SAGE' && <Database className="h-4 w-4 text-emerald-500" />}
              {category === 'Procurement AI' && <Sparkles className="h-4 w-4 text-purple-500" />}
              {category}
            </CardTitle>
            <CardDescription>
              {category === 'SAGE' && 'Sincronizações com o sistema SAGE'}
              {category === 'Procurement AI' && 'Processamento de dados para sugestões de compra'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Estado</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Horário</TableHead>
                  <TableHead>Última Execução</TableHead>
                  <TableHead>Resultado</TableHead>
                  <TableHead className="text-right">Acções</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categoryJobs.map((job) => {
                  const meta = JOB_METADATA[job.job_id] || {};
                  const Icon = meta.icon || Clock;
                  const isRunning = job.status === 'running' || runningJob === job.job_id;
                  
                  return (
                    <TableRow key={job.job_id} data-testid={`job-row-${job.job_id}`}>
                      <TableCell>
                        <Switch
                          data-testid={`job-toggle-${job.job_id}`}
                          checked={job.enabled !== false}
                          onCheckedChange={(checked) => toggleJob(job.job_id, checked)}
                          disabled={isRunning}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${meta.bgColor || 'bg-slate-100'}`}>
                            <Icon className={`h-4 w-4 ${meta.color || 'text-slate-500'}`} />
                          </div>
                          <div>
                            <p className="font-medium">{job.name}</p>
                            <p className="text-xs text-slate-500">{job.description}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Calendar className="h-3 w-3" />
                            {job.schedule?.description || meta.schedule || '-'}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => openScheduleEditor(job.job_id)}
                            data-testid={`job-edit-schedule-${job.job_id}`}
                            title="Editar horário"
                          >
                            <Edit className="h-3 w-3 text-slate-400 hover:text-slate-600" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm">{formatDateTime(job.last_run?.started_at || job.last_run)}</p>
                          {job.last_run?.duration_seconds && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Timer className="h-3 w-3" />
                              {formatDuration(job.last_run.duration_seconds)}
                            </p>
                          )}
                          {job.enabled && job.next_run && (
                            <p className="text-xs text-slate-400" data-testid={`job-next-run-${job.job_id}`}>
                              Próxima: {formatDateTime(job.next_run)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell data-testid={`job-result-${job.job_id}`}>
                        {job.running_since ? getStatusBadge('running') : getStatusBadge(job.last_run?.status || job.status)}
                        {job.consecutive_failures > 0 && (
                          <Badge variant="destructive" className="ml-1 text-[10px]">
                            {job.consecutive_failures} falhas
                          </Badge>
                        )}
                        <JobRunDetails job={job} />
                        {job.job_id === 'fsm_catalog_sync' && <CatalogTestButton />}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => runJob(job.job_id)}
                          disabled={isRunning || (sageLock?.lock_held && category === 'SAGE')}
                          data-testid={`run-job-${job.job_id}`}
                        >
                          {isRunning ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              A executar...
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-1" />
                              Executar
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{jobs.filter(j => j.enabled !== false).length}</p>
              <p className="text-xs text-slate-500">Jobs Activos</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{jobs.filter(j => j.consecutive_failures > 0).length}</p>
              <p className="text-xs text-slate-500">Com Erros</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Loader2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{jobs.filter(j => j.status === 'running').length}</p>
              <p className="text-xs text-slate-500">A Executar</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${sageLock?.lock_held ? 'bg-amber-100' : 'bg-slate-100'}`}>
              {sageLock?.lock_held ? (
                <Lock className="h-5 w-5 text-amber-600" />
              ) : (
                <Unlock className="h-5 w-5 text-slate-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-bold">{sageLock?.lock_held ? 'Ocupado' : 'Livre'}</p>
              <p className="text-xs text-slate-500">Lock SAGE</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmAction === 'release' ? 'Forçar Libertação do Lock' : 'Cancelar Syncs Stale'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'release' 
                ? 'Tem a certeza que quer forçar a libertação do lock SAGE? Isto pode causar problemas se houver uma sincronização real em curso.'
                : 'Tem a certeza que quer cancelar todos os syncs stale? Isto vai marcar como cancelados os syncs que estão "stuck".'
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmAction === 'release' ? forceReleaseLock : cancelStaleSyncs}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Editor Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Editar Horário
            </DialogTitle>
            <DialogDescription>
              {editingJob?.name}
              {editingJob?.isDefault && (
                <Badge variant="secondary" className="ml-2 text-xs">Default</Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Schedule Type */}
            <div className="space-y-2">
              <Label>Tipo de Agendamento</Label>
              <Select 
                value={scheduleForm.type} 
                onValueChange={(v) => setScheduleForm({...scheduleForm, type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cron">Hora Fixa (Cron)</SelectItem>
                  <SelectItem value="interval">Intervalo Regular</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Cron Options - Multiple Fixed Times */}
            {scheduleForm.type === 'cron' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Horas Fixas (até 6 por dia)</Label>
                  {scheduleForm.cronTimes.length < 6 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setScheduleForm({
                        ...scheduleForm,
                        cronTimes: [...scheduleForm.cronTimes, '12:00']
                      })}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Adicionar
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {scheduleForm.cronTimes.map((time, idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <Input
                        type="time"
                        value={time}
                        onChange={(e) => {
                          const newTimes = [...scheduleForm.cronTimes];
                          newTimes[idx] = e.target.value;
                          setScheduleForm({...scheduleForm, cronTimes: newTimes});
                        }}
                        className="h-9"
                      />
                      {scheduleForm.cronTimes.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            const newTimes = scheduleForm.cronTimes.filter((_, i) => i !== idx);
                            setScheduleForm({...scheduleForm, cronTimes: newTimes});
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">O job executará em cada uma destas horas, nos dias seleccionados</p>
              </div>
            )}
            
            {/* Interval Options */}
            {scheduleForm.type === 'interval' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Intervalo (minutos)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={1440}
                      value={scheduleForm.minutes}
                      onChange={(e) => setScheduleForm({...scheduleForm, minutes: e.target.value, hours: 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ou Horas</Label>
                    <Input
                      type="number"
                      min={0}
                      max={24}
                      value={scheduleForm.hours}
                      onChange={(e) => setScheduleForm({...scheduleForm, hours: e.target.value, minutes: 0})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hora Início</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={scheduleForm.hour_start}
                      onChange={(e) => setScheduleForm({...scheduleForm, hour_start: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hora Fim</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={scheduleForm.hour_end}
                      onChange={(e) => setScheduleForm({...scheduleForm, hour_end: e.target.value})}
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Days of Week */}
            <div className="space-y-2">
              <Label>Dias da Semana</Label>
              <Select 
                value={scheduleForm.day_of_week} 
                onValueChange={(v) => setScheduleForm({...scheduleForm, day_of_week: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="*">Todos os dias</SelectItem>
                  <SelectItem value="mon-fri">Segunda a Sexta</SelectItem>
                  <SelectItem value="mon-sat">Segunda a Sábado</SelectItem>
                  <SelectItem value="mon-sun">Segunda a Domingo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-3 bg-green-50 rounded-lg text-sm text-green-800">
              <CheckCircle className="h-4 w-4 inline mr-1" />
              As alterações são aplicadas <strong>imediatamente</strong> (hot-reload).
            </div>
          </div>
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="ghost" 
              onClick={resetSchedule}
              disabled={editingJob?.isDefault}
              className="text-slate-500"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Repor Default
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={saveSchedule} disabled={savingSchedule}>
                {savingSchedule ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Guardar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SyncManagement;
